import { z } from 'zod';
import {
  BOARD_SCHEMA_VERSION,
  BOARD_ID_PATTERN,
  MAX_TILES_PER_BOARD,
  MAX_GRID_ROWS,
} from './constants.js';
import { TileSchema } from './tile.js';

/**
 * H0 — the board envelope schema (ADR-002 §6.1).
 *
 * A board = grid metadata + a layout (positions) + tiles (identities/configs).
 * `layout` is deliberately SEPARATE from `tiles` (position vs identity) so the
 * immutable layout engine can produce a new layout object on every drag without
 * ever rewriting a tile config (ADR-001; ADR-002 §6.1).
 *
 * SECURITY (ADR-002 §7): `.strict()` (no smuggled fields), a hard tile-count cap
 * (#4/#8), unique tile ids, and referential integrity between layout and tiles.
 * No PII / URLs / secrets anywhere — the board is designed to be shared/cloned by
 * strangers (#7).
 */

const gridSchema = z
  .object({
    cols: z.number().int().min(1).max(24),
    rowHeight: z.number().int().min(1).max(1000),
    gap: z.number().int().min(0).max(64),
  })
  .strict();

const layoutEntrySchema = z
  .object({
    tileId: z.string(),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    w: z.number().int().min(1).max(6),
    h: z.number().int().min(1).max(6),
  })
  .strict();

const isoDate = z.string().datetime();

export const BoardSchema = z
  .object({
    schemaVersion: z.literal(BOARD_SCHEMA_VERSION),
    id: z.string().regex(BOARD_ID_PATTERN),
    createdAt: isoDate,
    updatedAt: isoDate,
    grid: gridSchema,
    layout: z.array(layoutEntrySchema).max(MAX_TILES_PER_BOARD),
    tiles: z.array(TileSchema).max(MAX_TILES_PER_BOARD),
  })
  .strict()
  .superRefine((board, ctx) => {
    // --- unique tile ids (ADR-002 §3: id unique within a board) ---
    const ids = new Set<string>();
    board.tiles.forEach((tile, i) => {
      if (ids.has(tile.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tiles', i, 'id'],
          message: `duplicate tile id "${tile.id}"`,
        });
      }
      ids.add(tile.id);
    });

    // --- referential integrity: every layout entry names a real tile ---
    board.layout.forEach((entry, i) => {
      if (!ids.has(entry.tileId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['layout', i, 'tileId'],
          message: `layout references unknown tile "${entry.tileId}"`,
        });
      }
    });

    // --- grid bounds: entries must fit within grid.cols and MAX_GRID_ROWS (H2) ---
    board.layout.forEach((entry, i) => {
      if (entry.x + entry.w > board.grid.cols) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['layout', i, 'x'],
          message: `tile "${entry.tileId}" extends beyond grid (x:${entry.x}+w:${entry.w}=${entry.x + entry.w} > cols:${board.grid.cols})`,
        });
      }
      if (entry.y + entry.h > MAX_GRID_ROWS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['layout', i, 'y'],
          message: `tile "${entry.tileId}" extends beyond max rows (y:${entry.y}+h:${entry.h}=${entry.y + entry.h} > MAX_GRID_ROWS:${MAX_GRID_ROWS})`,
        });
      }
    });
  });

/** Inferred output type — a validated, immutable board. */
export type Board = Readonly<z.infer<typeof BoardSchema>>;

/** Throwing parse. */
export function parseBoard(input: unknown): Board {
  return BoardSchema.parse(input);
}

/** Non-throwing parse — for graceful-degradation boundaries (ADR-002 §4). */
export function safeParseBoard(input: unknown): z.SafeParseReturnType<unknown, Board> {
  return BoardSchema.safeParse(input);
}
